import type {
  Database,
  Company,
  User,
  Project,
  ContractItem,
  InterimApplication,
  Tender,
  RetentionLedger,
  Task,
  TaskComment,
  Notification,
  ShareToken,
  RateLibrary,
  MainContractor,
  Contact,
  EstimatingTeamMember,
  Supplier,
  TermsTemplate,
  RateLibraryItem,
  RICSBenchmark,
  CommercialHealthMetric,
  Employer,
  ClientEmployerRelationship,
  EntitlementAlert,
  ActiveDispute,
  ClientRequest,
  PendingDocumentUpload,
} from '../../../types/database';
import type { Claim, ClaimCorrespondence, ClaimSummaryMetrics, ClaimFilters } from '../../../types/claims';
import type { Document } from '../../../types/documents';
import type {
  Activity,
  ActivityFilter,
  ActivityStats,
  CreatePhoneCallData,
  CreateMeetingData,
  CreateEmailData,
  CreateNoteData,
  CreateCommentData,
} from '../../../types/activity';
import { ProjectFoldersService } from '../../ProjectFoldersService';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';
import { geocodeAddress } from '../../../utils/geocoding';
import type { Constructor } from './mixins';
import { decorateNotificationWithPolicy, shouldPersistNotificationType } from '../../../notifications/policy';

export const withNotifications = <TBase extends Constructor<any>>(Base: TBase) => class Notifications extends Base {
  // NOTIFICATIONS
  // ============================================================================

  public getNotifications(): Notification[] {
    return Object.values(this.db.notifications);
  }

  public getNotification(id: string): Notification | undefined {
    return this.db.notifications[id];
  }

  public getNotificationsByUser(userId: string): Notification[] {
    return this.getNotifications()
      .filter(n => n.userId === userId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  public getUnreadNotifications(userId: string): Notification[] {
    return this.getNotificationsByUser(userId).filter(n => !n.read);
  }

  public createNotification(notification: Omit<Notification, 'id' | 'createdAt'>): Notification {
    if (!shouldPersistNotificationType(notification.type)) {
      return {
        ...notification,
        id: 'suppressed-notification',
        createdAt: new Date().toISOString(),
      } as Notification;
    }

    // Validate that the user exists before creating the notification
    const user = this.db.users[notification.userId];
    if (!user) {
      console.warn(`⚠️  Cannot create notification: User ${notification.userId} does not exist. Skipping notification creation.`);
      // Return a dummy notification that won't be saved
      return {
        ...notification,
        id: 'invalid-notification',
        createdAt: new Date().toISOString(),
      } as Notification;
    }
    
    const decorated = decorateNotificationWithPolicy(notification);
    const newNotification: Notification = {
      ...decorated,
      id: this.generateId(),
      createdAt: new Date().toISOString(),
    };
    this.db.notifications[newNotification.id] = newNotification;
    this.saveToLocalStorage();
    // Strip undefined values before sending to backend to avoid DB constraint errors
    const payload: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(newNotification)) {
      if (v !== undefined) payload[k] = v;
    }
    this.saveEntityToBackend('notifications', newNotification.id, payload as unknown as Notification, true);
    this.notifyListeners();
    return newNotification;
  }

  public markNotificationAsRead(id: string): Notification | undefined {
    const notification = this.db.notifications[id];
    if (!notification) return undefined;
    
    const updated = {
      ...notification,
      read: true,
      readAt: new Date().toISOString(),
    };
    this.db.notifications[id] = updated;
    this.saveToLocalStorage();
    this.saveEntityToBackend('notifications', id, updated, false);
    this.notifyListeners();
    return updated;
  }

  public markAllNotificationsAsRead(userId: string): void {
    const userNotifications = this.getNotificationsByUser(userId);
    userNotifications.forEach(notification => {
      if (!notification.read) {
        const updated = {
          ...notification,
          read: true,
          readAt: new Date().toISOString(),
        };
        this.db.notifications[notification.id] = updated;
        this.saveEntityToBackend('notifications', notification.id, updated, false);
      }
    });
    this.saveToLocalStorage();
    this.notifyListeners();
  }

  public deleteNotification(id: string): boolean {
    const notification = this.db.notifications[id];
    if (!notification) return false;
    delete this.db.notifications[id];
    this.saveToLocalStorage();
    this.deleteEntityFromBackend('notifications', id);
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // SHARE TOKENS
  // ============================================================================

  public getShareTokens(): ShareToken[] {
    return Object.values(this.db.shareTokens);
  }

  public getShareToken(id: string): ShareToken | undefined {
    return this.db.shareTokens[id];
  }

  public getShareTokenByToken(token: string): ShareToken | undefined {
    return this.getShareTokens().find(t => t.token === token);
  }

  public getShareTokensByProject(projectId: string): ShareToken[] {
    return this.getShareTokens().filter(t => t.projectId === projectId);
  }

  public createShareToken(shareToken: Omit<ShareToken, 'id' | 'createdAt' | 'updatedAt'>): ShareToken {
    const newToken: ShareToken = {
      ...shareToken,
      id: this.generateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this.db.shareTokens[newToken.id] = newToken;
    this.saveToStorage();
    this.notifyListeners();
    return newToken;
  }

  public updateShareToken(id: string, updates: Partial<ShareToken>): ShareToken | undefined {
    const token = this.db.shareTokens[id];
    if (!token) return undefined;
    
    const updated = {
      ...token,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.shareTokens[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteShareToken(id: string): boolean {
    if (!this.db.shareTokens[id]) return false;
    delete this.db.shareTokens[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================
};
